/**
 * Moving through a game's history.
 *
 * Which ply is on screen is a number, and `LIVE` is the one value that means "whatever
 * the latest one turns out to be". That distinction matters more than it looks: setting
 * the ply to `moves.length` pins the board to a position that is current *now*, and the
 * next move to arrive would leave it behind without anything on screen saying so. Going
 * to the end has to mean following the end.
 *
 * Every screen that shows a board shares this, so stepping behaves identically in a live
 * match, a finished one, a game against the bot, and two bots playing each other.
 */

import { useEffect } from 'react';

/** The live position, rather than a fixed ply of it. */
export const LIVE = Infinity;

/** Keys that a text field has a better claim to than we do. */
const TYPING = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return el.isContentEditable || TYPING.has(el.tagName);
}

export interface HistoryKeys {
  /** The ply currently shown. `LIVE` counts as the last one. */
  readonly ply: number;
  readonly moveCount: number;
  seek(ply: number): void;
  /** Off while there is nothing to step through, or on a screen that owns the keys. */
  readonly enabled?: boolean;
}

/**
 * Arrow keys for stepping: left and right by one move, up and down to either end.
 *
 * Bound to the window rather than to the board, because the thing you are most likely to
 * be looking at when you want to step is the move list, and requiring a click on the
 * board first would make the shortcut useless exactly when it is wanted. The cost of that
 * is having to be careful about who else wants these keys — hence the guards below.
 */
export function useHistoryKeys(
  { ply, moveCount, seek, enabled = true }: HistoryKeys,
): void {
  useEffect(() => {
    if (!enabled || moveCount === 0) return;

    const onKeyDown = (event: KeyboardEvent) => {
      // Someone is writing a chat message, or picking a formation from a select.
      if (isTyping(event.target)) return;
      // Cmd+Left is "back" and Alt+Left is too; a shortcut that eats browser navigation
      // is worse than no shortcut.
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const at = Math.min(ply, moveCount);
      let to: number;

      switch (event.key) {
        case 'ArrowLeft':
          to = Math.max(0, at - 1);
          break;
        case 'ArrowRight':
          to = at + 1;
          break;
        case 'ArrowUp':
        case 'Home':
          to = 0;
          break;
        case 'ArrowDown':
        case 'End':
          to = LIVE;
          break;
        default:
          return;
      }

      // Stepping past the last move is not a different position, it is the live one —
      // and it has to follow the game rather than sit on a ply that is about to be stale.
      seek(to >= moveCount ? LIVE : to);
      // Only once we know it was ours: the arrow keys still scroll every other page.
      event.preventDefault();
    };

    globalThis.addEventListener('keydown', onKeyDown);
    return () => globalThis.removeEventListener('keydown', onKeyDown);
  }, [ply, moveCount, seek, enabled]);
}
